import Client from '@spike/client-model';
import { AppointmentsListStatus, countThunk, reset } from '@spike/appointments-list-action';
import type { StatusCount } from '@spike/appointments-list-model';
import { showErrorThunk } from '@spike/notifications-action';
import { useNonInitialEffect } from '@versiondos/hooks';
import { AppointmentsList } from 'components/Bookings2/List/AppointmentsList';
import { allAllTab } from 'components/Bookings2/List/tabs';
import Spinner from 'components/UI/Spinner';
import { useApiClientWrapper } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './LeadBookingTab.module.scss';

interface LeadBookingTabProps {
    lead: Client;
    onBookAppointment: () => void;
    refreshSignal?: number;
    scrollableContainerId?: string;
}

type ProbeState = 'loading' | 'empty' | 'populated' | 'error';

const PROBE_SERVICE_CATEGORY = allAllTab.serviceCategory;
const PROBE_STATUS_IDS: Array<number> = [...allAllTab.statusIds];

const LeadBookingTab: FunctionComponent<LeadBookingTabProps> = ({
    lead,
    onBookAppointment,
    refreshSignal,
    scrollableContainerId
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const [probeState, setProbeState] = useState<ProbeState>('loading');

    const appointmentsListStatus = useSelector<RootState, AppointmentsListStatus>(
        state => state.appointmentsList.status
    );
    const countByStatus = useSelector<RootState, Array<StatusCount>>(
        state => state.appointmentsList.client.countByStatus
    );
    const probeIdRef = useRef(0);
    const awaitingProbeRef = useRef(false);
    const silentRef = useRef(false);
    const mountedRef = useRef(true);

    const leadId = lead.id;

    const runProbe = useCallback(
        (silent: boolean) => {
            if (leadId === undefined) {
                setProbeState('empty');
                return;
            }

            probeIdRef.current += 1;
            awaitingProbeRef.current = true;
            silentRef.current = silent;

            if (!silent) {
                setProbeState('loading');
            }

            dispatch(
                countThunk(
                    apiClientWrapper,
                    'client',
                    PROBE_SERVICE_CATEGORY,
                    organizationMarketplaces.map(marketplace => marketplace.id),
                    null,
                    null,
                    PROBE_STATUS_IDS,
                    [],
                    [],
                    leadId,
                    null
                )
            );
        },
        [leadId, dispatch, organizationMarketplaces]
    );

    useNonInitialEffect(() => {
        if (!mountedRef.current || !awaitingProbeRef.current) {
            return;
        }

        switch (appointmentsListStatus) {
            case AppointmentsListStatus.ClientCountSuccess: {
                awaitingProbeRef.current = false;
                const total = countByStatus.reduce((sum, status) => sum + status.count, 0);

                if (total > 0) {
                    setProbeState('populated');
                } else if (!silentRef.current) {
                    setProbeState('empty');
                }
                break;
            }
            case AppointmentsListStatus.ClientError: {
                awaitingProbeRef.current = false;
                if (!silentRef.current) {
                    setProbeState('error');
                    dispatch(showErrorThunk('The bookings could not be loaded. Please try again.'));
                }
                break;
            }
        }
    }, [appointmentsListStatus]);

    useEffect(() => {
        mountedRef.current = true;
        runProbe(false);

        return () => {
            mountedRef.current = false;
            dispatch(reset('client'));
        };
    }, []);

    useNonInitialEffect(() => {
        if (probeState === 'empty' || probeState === 'error') {
            runProbe(probeState === 'empty');
        }
    }, [refreshSignal]);

    if (probeState === 'loading') {
        return (
            <div className={styles.root}>
                <div className={styles.stateContainer}>
                    <Spinner />
                </div>
            </div>
        );
    }

    if (probeState === 'populated' && leadId !== undefined) {
        return (
            <div className={styles.historyContainer}>
                <AppointmentsList
                    listType="client"
                    clientId={leadId}
                    scrollableContainerId={scrollableContainerId}
                />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Booking</h3>

                <div className={styles.card}>
                    {probeState === 'error' ? (
                        <div className={styles.errorContainer}>
                            <p className={styles.empty}>The bookings could not be loaded.</p>
                            <button
                                type="button"
                                className={styles.retry}
                                onClick={() => runProbe(false)}
                            >
                                Try Again
                            </button>
                        </div>
                    ) : (
                        <div className={styles.emptyRow}>
                            <p className={styles.empty}>This lead has not booked an appointment yet</p>
                            <button
                                type="button"
                                className={styles.bookButton}
                                onClick={onBookAppointment}
                            >
                                Book Appointment
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LeadBookingTab;
